import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { format, formatDistanceToNowStrict } from "date-fns";
import {
  BadgeCheck,
  ChevronDown,
  Copy,
  ExternalLink,
  FileCheck2,
  Loader2,
  Mail,
  MessageCircle,
  Phone,
  RefreshCw,
  Search,
  Send,
} from "lucide-react";
import { toast } from "sonner";
import { AdminShell } from "@/components/admin/AdminShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { buildWhatsAppUrl } from "@/lib/phone";
import { listInboxThreads, getStudentThread, sendAdminMessage } from "@/lib/admin-students.functions";

export const Route = createFileRoute("/admin/inbox")({
  component: AdminInboxPage,
  head: () => ({
    meta: [
      { title: "Student Inbox · WeLivdIt Admin" },
      { name: "description", content: "Reply to student messages with payment verification context in one view." },
      { property: "og:title", content: "Student Inbox · WeLivdIt Admin" },
      { property: "og:description", content: "Reply to student messages with payment verification context in one view." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
});

type Thread = Awaited<ReturnType<typeof listInboxThreads>>["threads"][number];
type ThreadDetail = Awaited<ReturnType<typeof getStudentThread>>;
type Filter = "all" | "unread" | "paid" | "waiting";

const FILTERS: { key: Filter; label: string }[] = [
  { key: "all", label: "All" },
  { key: "unread", label: "Unread" },
  { key: "paid", label: "Paid Students" },
  { key: "waiting", label: "Waiting for Reply" },
];

const QUICK_REPLIES: { label: string; text: (name: string) => string }[] = [
  {
    label: "Book your 1-on-1 mock slot",
    text: (n) =>
      `Hi ${n}, please book your 1-on-1 mock slot from your student portal. Pick a time that suits you and we'll take it from there.`,
  },
  {
    label: "Live DS-160 screen-share instructions",
    text: (n) =>
      `Hi ${n}, we review your DS-160 live over screen-share during your 1-on-1 session. Keep your DS-160 open on your laptop when we join — no need to send any files.`,
  },
  {
    label: "Visa slot confirmation",
    text: (n) =>
      `Hi ${n}, thanks for sharing your visa slot. Please make sure your interview date is updated in your student portal so we can plan your mocks around it.`,
  },
  {
    label: "Payment acknowledged",
    text: (n) =>
      `Hi ${n}, we've received and verified your payment. Welcome to WeLivdIt Visa Prep! Your plan is now active in your student portal.`,
  },
];

function initials(name: string) {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase())
      .join("") || "S"
  );
}

function planTone(plan: string | null | undefined) {
  const p = (plan ?? "").toLowerCase();
  if (p.includes("refusal")) return "bg-amber-500/15 text-amber-700 dark:text-amber-400";
  if (p.includes("full")) return "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400";
  if (p.includes("basic") || p.includes("fresher")) return "bg-sky-500/15 text-sky-700 dark:text-sky-400";
  return "bg-muted text-muted-foreground";
}

function planLabel(plan: string | null | undefined) {
  const p = (plan ?? "").toLowerCase();
  if (p.includes("refusal")) return "Refusal";
  if (p.includes("full")) return "Full Support";
  if (p.includes("basic") || p.includes("fresher")) return "Basic";
  return plan ?? "";
}

function PlanBadge({ plan }: { plan: string | null | undefined }) {
  if (!plan) return null;
  return (
    <span className={`shrink-0 rounded-md px-1.5 py-0.5 text-[10px] font-bold ${planTone(plan)}`}>{planLabel(plan)}</span>
  );
}

function AdminInboxPage() {
  const listThreads$ = useServerFn(listInboxThreads);
  const getThread$ = useServerFn(getStudentThread);
  const send$ = useServerFn(sendAdminMessage);

  const [threads, setThreads] = useState<Thread[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  const [activeId, setActiveId] = useState<string | null>(null);
  const [detail, setDetail] = useState<ThreadDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const streamRef = useRef<HTMLDivElement>(null);

  const loadThreads = useCallback(async () => {
    try {
      const res = await listThreads$({});
      setThreads(res.threads);
    } catch (e) {
      console.error(e);
      toast.error("Couldn't load the inbox");
    } finally {
      setLoading(false);
    }
  }, [listThreads$]);

  const openThread = useCallback(
    async (userId: string) => {
      setActiveId(userId);
      setLoadingDetail(true);
      try {
        const res = await getThread$({ data: { userId } });
        setDetail(res);
        setThreads((prev) => prev.map((t) => (t.user_id === userId ? { ...t, unread_count: 0 } : t)));
      } catch (e) {
        console.error(e);
        toast.error("Couldn't open this conversation");
      } finally {
        setLoadingDetail(false);
      }
    },
    [getThread$],
  );

  useEffect(() => {
    void loadThreads();
  }, [loadThreads]);

  useEffect(() => {
    streamRef.current?.scrollTo({ top: streamRef.current.scrollHeight });
  }, [detail]);

  const counts = useMemo(
    () => ({
      all: threads.length,
      unread: threads.filter((t) => t.unread_count > 0).length,
      paid: threads.filter((t) => t.is_paid).length,
      waiting: threads.filter((t) => !t.last_from_admin).length,
    }),
    [threads],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const qd = q.replace(/\D/g, "");
    return threads.filter((t) => {
      if (filter === "unread" && t.unread_count === 0) return false;
      if (filter === "paid" && !t.is_paid) return false;
      if (filter === "waiting" && t.last_from_admin) return false;
      if (!q) return true;
      return (
        (t.name ?? "").toLowerCase().includes(q) ||
        (t.email ?? "").toLowerCase().includes(q) ||
        (qd.length >= 3 && (t.phone ?? "").replace(/\D/g, "").includes(qd))
      );
    });
  }, [threads, search, filter]);

  const send = async () => {
    const content = draft.trim();
    if (!content || !activeId) return;
    setSending(true);
    try {
      await send$({ data: { userId: activeId, content } });
      setDraft("");
      await openThread(activeId);
      void loadThreads();
    } catch (e) {
      console.error(e);
      toast.error("Couldn't send the reply");
    } finally {
      setSending(false);
    }
  };

  const student = detail?.student;
  const activeThread = threads.find((t) => t.user_id === activeId);
  const firstName = (student?.name ?? "there").split(" ")[0];
  const wa = student?.phone ? buildWhatsAppUrl(student.phone, `Hi ${firstName}, this is WeLivdIt Visa Prep.`) : null;

  return (
    <AdminShell
      title="Student Inbox"
      description="Every student message with payment verification in the same view."
      actions={
        <Button variant="outline" size="sm" onClick={() => void loadThreads()} className="gap-1.5">
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </Button>
      }
    >
      <div className="grid h-[calc(100vh-13rem)] min-h-[560px] gap-3 lg:grid-cols-[300px_minmax(0,1fr)_300px]">
        {/* Left: conversation list */}
        <div className="flex min-h-0 flex-col overflow-hidden rounded-2xl border border-border bg-card">
          <div className="space-y-2 border-b border-border p-3">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, email or phone…"
                className="pl-8"
              />
            </div>
            <div className="flex flex-wrap gap-1">
              {FILTERS.map((f) => (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => setFilter(f.key)}
                  className={`rounded-full px-2.5 py-1 text-[11px] font-semibold transition ${
                    filter === f.key
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {f.label} <span className="opacity-70">{counts[f.key]}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="min-h-0 flex-1 divide-y divide-border overflow-y-auto">
            {loading && (
              <p className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading conversations…
              </p>
            )}
            {!loading && filtered.length === 0 && (
              <p className="p-4 text-sm text-muted-foreground">No conversations here.</p>
            )}
            {filtered.map((t) => (
              <button
                key={t.user_id}
                type="button"
                onClick={() => void openThread(t.user_id)}
                className={`flex w-full gap-3 p-3 text-left transition hover:bg-muted/60 ${
                  activeId === t.user_id ? "bg-muted" : ""
                }`}
              >
                <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-primary/10 text-xs font-bold text-primary">
                  {initials(t.name)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className={`truncate text-sm ${t.unread_count > 0 ? "font-bold" : "font-medium"}`}>
                      {t.name}
                    </span>
                    <PlanBadge plan={t.plan} />
                    <span className="ml-auto shrink-0 text-[10px] text-muted-foreground">
                      {formatDistanceToNowStrict(new Date(t.last_message_at))}
                    </span>
                  </div>
                  <div className="mt-0.5 flex items-center gap-2">
                    <p className="line-clamp-1 flex-1 text-xs text-muted-foreground">
                      {t.last_from_admin ? "You: " : ""}
                      {t.last_message}
                    </p>
                    {t.unread_count > 0 && (
                      <span className="inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-primary px-1.5 text-[10px] font-bold text-primary-foreground">
                        {t.unread_count}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Center: chat stream */}
        <div className="flex min-h-0 flex-col overflow-hidden rounded-2xl border border-border bg-card">
          {!activeId ? (
            <p className="m-auto px-6 text-center text-sm text-muted-foreground">
              Pick a conversation to read and reply.
            </p>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-3 border-b border-border p-3">
                <div className="grid h-10 w-10 place-items-center rounded-full bg-primary/10 text-sm font-bold text-primary">
                  {initials(student?.name ?? activeThread?.name ?? "S")}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="truncate font-semibold">{student?.name ?? activeThread?.name ?? "Student"}</p>
                    <PlanBadge plan={student?.plan ?? activeThread?.plan} />
                  </div>
                  <div className="flex flex-wrap gap-x-3 text-xs text-muted-foreground">
                    {student?.email && (
                      <a href={`mailto:${student.email}`} className="inline-flex items-center gap-1 hover:underline">
                        <Mail className="h-3 w-3" /> {student.email}
                      </a>
                    )}
                    {student?.phone && (
                      <a href={`tel:${student.phone}`} className="inline-flex items-center gap-1 hover:underline">
                        <Phone className="h-3 w-3" /> {student.phone}
                      </a>
                    )}
                  </div>
                </div>
                {wa && (
                  <a href={wa} target="_blank" rel="noreferrer">
                    <Button size="sm" variant="outline" className="gap-1.5 text-emerald-600">
                      <MessageCircle className="h-4 w-4" /> WhatsApp
                    </Button>
                  </a>
                )}
              </div>

              <div ref={streamRef} className="min-h-0 flex-1 space-y-3 overflow-y-auto bg-muted/20 p-4">
                {loadingDetail && !detail && (
                  <p className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading messages…
                  </p>
                )}
                {detail?.messages.map((m) => (
                  <div key={m.id} className={`flex ${m.is_from_admin ? "justify-end" : "justify-start"}`}>
                    <div
                      className={`max-w-[78%] rounded-2xl px-4 py-2 text-sm shadow-sm ${
                        m.is_from_admin
                          ? "rounded-br-sm bg-primary text-primary-foreground"
                          : "rounded-bl-sm border border-border bg-card text-foreground"
                      }`}
                    >
                      <p className="whitespace-pre-wrap leading-relaxed">{m.content}</p>
                      <p
                        className={`mt-1 text-[10px] ${
                          m.is_from_admin ? "text-primary-foreground/70" : "text-muted-foreground"
                        }`}
                      >
                        {m.is_from_admin ? "You · " : ""}
                        {format(new Date(m.created_at), "d MMM, p")}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="space-y-2 border-t border-border p-3">
                <div className="flex flex-wrap gap-1.5">
                  {QUICK_REPLIES.map((q) => (
                    <button
                      key={q.label}
                      type="button"
                      onClick={() => setDraft(q.text(firstName))}
                      className="rounded-full border border-border px-2.5 py-1 text-[11px] font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
                    >
                      {q.label}
                    </button>
                  ))}
                </div>
                <div className="flex items-end gap-2">
                  <Textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    rows={2}
                    placeholder="Reply… (Ctrl/⌘ + Enter to send)"
                    className="resize-none"
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                        e.preventDefault();
                        void send();
                      }
                    }}
                  />
                  <Button onClick={() => void send()} disabled={sending || !draft.trim()} className="gap-1.5">
                    {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Right: dossier */}
        <div className="min-h-0 space-y-3 overflow-y-auto rounded-2xl border border-border bg-card p-3">
          {!student ? (
            <p className="p-3 text-sm text-muted-foreground">Student details appear here.</p>
          ) : (
            <>
              <Section title="Student profile">
                <Fact label="University" value={student.university} />
                <Fact label="Degree" value={student.degree} />
                <Fact label="Intake" value={student.intakeTerm} />
                <Fact label="Consulate" value={student.consulate} />
                <Fact
                  label="Biometrics"
                  value={
                    [student.biometricsDate, student.biometricsCity].filter(Boolean).join(" · ") ||
                    (student.hasBookedInterview === "no" ? "Not booked" : null)
                  }
                />
                <Fact
                  label="Interview"
                  value={student.visaDate ?? (student.hasBookedInterview === "no" ? "Not booked" : null)}
                />
                <Fact label="Funding" value={student.fundingSource} />
                {student.priorRefusals ? <Fact label="Prior refusals" value={String(student.priorRefusals)} /> : null}
              </Section>

              <Section title="Payments">
                {student.paymentProofUrl ? (
                  <div className="space-y-2 rounded-lg border border-border bg-background p-2">
                    <a href={student.paymentProofUrl} target="_blank" rel="noopener noreferrer" className="block">
                      <img
                        src={student.paymentProofUrl}
                        alt="Payment receipt"
                        className="max-h-40 w-full rounded-md object-contain bg-muted"
                        onError={(e) => ((e.target as HTMLImageElement).style.display = "none")}
                      />
                    </a>
                    <div className="flex gap-1.5">
                      <a href={student.paymentProofUrl} target="_blank" rel="noopener noreferrer" className="flex-1">
                        <Button size="sm" variant="outline" className="h-7 w-full gap-1 text-xs">
                          <FileCheck2 className="h-3 w-3" /> Open <ExternalLink className="h-3 w-3" />
                        </Button>
                      </a>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 gap-1 text-xs"
                        onClick={() => {
                          void navigator.clipboard.writeText(student.paymentProofUrl as string);
                          toast.success("Receipt link copied");
                        }}
                      >
                        <Copy className="h-3 w-3" /> Copy link
                      </Button>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">No receipt uploaded.</p>
                )}
                {detail?.payments.length === 0 && <p className="text-xs text-muted-foreground">No transactions.</p>}
                {detail?.payments.map((p) => (
                  <div key={p.id} className="rounded-lg border border-border bg-background p-2 text-xs">
                    <div className="flex items-center gap-1.5">
                      <span
                        className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-semibold ${
                          p.status === "paid"
                            ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400"
                            : "bg-muted text-muted-foreground"
                        }`}
                      >
                        {p.status === "paid" && <BadgeCheck className="h-3 w-3" />}
                        {p.status === "paid" ? "Verified" : p.status}
                      </span>
                      <span className="font-semibold">₹{p.amountInr.toLocaleString("en-IN")}</span>
                      <span className="ml-auto text-muted-foreground">{format(new Date(p.createdAt), "d MMM yy")}</span>
                    </div>
                    <p className="mt-1 font-medium">{p.planName}</p>
                    <div className="mt-1 flex items-center gap-1">
                      <span className="truncate font-mono text-[10px] text-muted-foreground">
                        {p.razorpayPaymentId ?? p.razorpayOrderId}
                      </span>
                      {p.receiptUrl && (
                        <button
                          type="button"
                          aria-label="Copy receipt link"
                          onClick={() => {
                            void navigator.clipboard.writeText(p.receiptUrl as string);
                            toast.success("Receipt link copied");
                          }}
                          className="ml-auto rounded p-0.5 hover:bg-muted"
                        >
                          <Copy className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </Section>

              <Section title="Intake responses" defaultOpen={false}>
                {student.intakeAnswers.length === 0 ? (
                  <p className="text-xs text-muted-foreground">No intake form on file.</p>
                ) : (
                  student.intakeAnswers.map((r) => (
                    <div key={r.q} className="border-b border-border/50 pb-1.5 text-xs last:border-0">
                      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{r.q}</p>
                      <p className="break-words">{r.a}</p>
                    </div>
                  ))
                )}
              </Section>
            </>
          )}
        </div>
      </div>
    </AdminShell>
  );
}

function Section({
  title,
  children,
  defaultOpen = true,
}: {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  return (
    <details open={defaultOpen} className="group rounded-xl border border-border">
      <summary className="flex cursor-pointer select-none items-center justify-between px-3 py-2 text-[11px] font-black uppercase tracking-wider text-muted-foreground">
        {title}
        <ChevronDown className="h-3.5 w-3.5 transition group-open:rotate-180" />
      </summary>
      <div className="space-y-2 px-3 pb-3">{children}</div>
    </details>
  );
}

function Fact({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="flex items-baseline justify-between gap-2 text-xs">
      <span className="shrink-0 text-muted-foreground">{label}</span>
      <span className="truncate text-right font-medium">{value || "—"}</span>
    </div>
  );
}
